import {
    Component,
    ComponentInterface,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Listen,
    Prop,
    State,
    Watch,
} from "@stencil/core";
import { scrollToTarget } from "../../utils/scrollToTarget";
import { AutoSuggestClickType } from "../autosuggest/types";

/** static base className */
const baseClass = "ux-iws-search-bar";

/**
 * @slot (default) - can be used to display content in the search bar panel
 * @slot buttons - can be used for additional buttons that should be displayed next to the submit button
 */

@Component({
    tag: "ux-iws-search-bar",
    styleUrl: "search-bar.scss",
})
export class SearchBar implements ComponentInterface {
    /** the searchbox dom element */
    @Element() domNode: HTMLUxIwsSearchBarElement;
    /** Create new custom reset event */
    @Event({
        eventName: "uxSearchBarReset",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    searchBarReset: EventEmitter;
    /** the input field name, should not be "source" */
    @Prop() name: string = "q";
    /** the input field id */
    @Prop() searchPreviewFieldId: string = "";
    /** the input field name */
    @Prop({ mutable: true, reflect: true }) searchString: string = "";
    /** CSS-style selector for the search form element */
    @Prop() searchFormSelector: string = "";
    /** CSS-style selector for the search categories navigation */
    @Prop() navTabContainerSelector: string = "";
    /** CSS-style selector for the result tabs */
    @Prop() resultTabContainerSelector: string = "";
    /** the input placeholder text */
    @Prop() placeholder: string = "";
    /** the submit button label */
    @Prop() buttonText: string = "";
    /** prevent the automatic scroll when this is handled by the tab tags component */
    @Prop() preventAutomaticScroll: boolean = false;
    /** if set, the search bar will not get fixed on top upon scrolling */
    @Prop() noSticky: boolean = false;
    /** if set, the input field will not be focused on component load, use for secondary search bars */
    @Prop() noInitialFocus: boolean = false;
    /** if set, content of the slot and the wrapper container will not be rendered */
    @Prop() noContent: boolean = false;
    /** if set, there will be a space between search bar and search button */
    @Prop() compact: boolean = false;
    /** if set, there will be no paddings or margins around the element */
    @Prop() noPadding: boolean = false;
    /** if available, an autosuggest will appear when 3 or more characters are typed in a search input field */
    @Prop() autosuggestEndpoint: string = "";
    /** if available, quick links and curated search terms will be displayed in the autosuggest */
    @Prop() quickLinksEndpoint: string = "";
    /** label for search results title in autosuggest */
    @Prop() resultsLabel: string = "Search Results";
    /** label for quickLinks */
    @Prop() quickLinksLabel: string = "Quick Links";
    /** label for curated search terms */
    @Prop() curatedSearchLabel: string = "Frequent Search Terms";
    /** label for the search input field */
    @Prop() searchInputLabel: string = "Search term";
    /** label for the search reset button */
    @Prop() searchResetLabel: string = "Reset search";

    /** flag used to control the reset button visibility */
    @State() hasText: boolean = false;
    /** flag that indicates if the searchbar is in sticky position at the page top */
    @State() isSticky: boolean = false;
    /** The height which has to be reserved if the searchbar is sticky */
    @State() barHeight: number = 0;
    /** Id of the active tab */
    @State() activeTabId: string = "";
    /** Flag that indicated whether an autosuggest should be displayed */
    @State() showAutosuggest: boolean = false;

    /** Initial search string */
    private initialSearchString: string = "";
    /** Search form element */
    private searchForm: HTMLFormElement | null = null;
    /** Search input element */
    private searchInput: HTMLInputElement | null = null;
    /** Navigation tabs */
    private navTabContainer: HTMLUxTabContainerElement | null = null;
    /** Result tabs */
    private resultTabContainer: HTMLUxTabContainerElement | null = null;

    private handleFormEvent = (e: Event) => {
        if (this.searchInput === null) return;
        switch (e.type) {
            case "submit":
                if (this.searchInput.value.length < 2) {
                    e.preventDefault();
                }
                break;
            case "input":
            case "change":
                this.searchString = this.searchInput.value;
                this.hasText = this.searchString.length > 0;
                break;
            default:
                break;
        }
    };

    private handleNavTabEvent = (e: Event) => {
        this.activeTabId = (e as any).detail;
        if (this.resultTabContainer !== null) {
            this.resultTabContainer.setActiveTab(this.activeTabId);
            this.scrollToResults();
        }
    };

    private resetForm = () => {
        if (this.searchInput === null) return;
        this.searchInput.value = "";
        this.searchString = this.searchInput.value;
        this.hasText = false;
        this.searchInput.focus();
        this.searchBarReset.emit();
    };

    private setSearchInput() {
        if (this.searchInput === null) {
            return;
        }
        this.hasText = this.searchString.length > 0;
        this.searchInput.value = this.searchString;
    }

    @Watch("searchFormSelector")
    setSearchForm() {
        if (this.searchForm !== null) {
            this.searchForm.removeEventListener("submit", this.handleFormEvent);
        }
        this.searchForm =
            this.searchFormSelector === ""
                ? this.domNode.closest<HTMLFormElement>("form")
                : document.querySelector<HTMLFormElement>(
                      this.searchFormSelector
                  );
        if (this.searchForm !== null) {
            this.searchForm.addEventListener("submit", this.handleFormEvent);
        }
    }

    @Watch("navTabContainerSelector")
    setNavTabContainer() {
        if (this.navTabContainer !== null) {
            this.navTabContainer.removeEventListener(
                "uxActiveTabSet",
                this.handleNavTabEvent
            );
        }
        this.navTabContainer =
            this.navTabContainerSelector === ""
                ? null
                : document.querySelector<HTMLUxTabContainerElement>(
                      this.navTabContainerSelector
                  );
        if (this.navTabContainer !== null) {
            this.navTabContainer.addEventListener(
                "uxActiveTabSet",
                this.handleNavTabEvent
            );
        }
    }

    @Watch("resultTabContainerSelector")
    setResultTabContainer() {
        this.resultTabContainer =
            this.resultTabContainerSelector === ""
                ? null
                : (document.querySelector(
                      this.resultTabContainerSelector
                  ) as HTMLUxTabContainerElement);
    }

    @Watch("isSticky")
    handleStickyChanged() {
        if (this.isSticky || this.barHeight === 0) {
            const el = this.domNode.querySelector(
                `.${baseClass}__inner`
            ) as HTMLDivElement;
            this.barHeight =
                el !== null ? el.getBoundingClientRect().height : 0;
        }
    }

    @Watch("activeTabId")
    handleTabChange() {
        if (this.preventAutomaticScroll === true) return;
        if (this.activeTabId !== "") {
            const offset = this.domNode.getBoundingClientRect().top + 30;
            if (offset >= 0) {
                return;
            }
            window.scrollBy(0, offset);
        }
    }

    @Watch("searchString")
    handleSearchStringChange() {
        if (this.searchString.length > 2 && this.autosuggestEndpoint !== "") {
            this.showAutosuggest = true;
        } else {
            this.showAutosuggest = false;
        }
    }

    @Watch("showAutosuggest")
    handleShowAutosuggestChange() {
        // make the browser ignore user input events on all elements except input, search form buttons and autosuggest when an autosuggest is shown
        const pageContents = document.querySelectorAll(
            ".ux-iws-header, #search-result-tabs, #search-nav-tabs, .ux-iws-search-results, .ux-iws-footer"
        );
        window.setTimeout(() => {
            if (this.showAutosuggest) {
                pageContents.forEach((el) => el.setAttribute("inert", "true"));
            } else {
                pageContents.forEach((el) => el.removeAttribute("inert"));
            }
        }, 100);
    }

    @Listen("scroll", { target: "window", passive: false })
    handleScroll() {
        if (this.noSticky) return;
        this.isSticky = this.domNode.getBoundingClientRect().top <= -30;
    }

    @Listen("click", { target: "document" })
    handleDocumentClick(e: Event) {
        const target = e.target as HTMLElement;
        const handleSearchItemClick = (e: Event, target: HTMLElement) => {
            const dataAttribute = "data-ux-iws-search";
            if (
                target.hasAttribute(dataAttribute) &&
                this.searchInput !== null &&
                this.searchForm !== null
            ) {
                e.preventDefault();
                this.searchInput.value = target.getAttribute(dataAttribute);
                this.searchForm.submit();
            }
        };

        const handleClickOutsideSearchbar = (target: HTMLElement) => {
            if (
                target.closest(".ux-iws-search-bar__controls") == null ||
                target.closest(".ux-iws-search-bar__autosuggest") == null
            ) {
                if (this.showAutosuggest === true) {
                    this.showAutosuggest = false;
                }
            }
        };

        handleSearchItemClick(e, target);
        handleClickOutsideSearchbar(target);
    }

    private handleAutosuggestionBtnClick = (event: CustomEvent) => {
        this.searchInput.value = event.detail.suggestion;
        const clickType: AutoSuggestClickType = event.detail.type;
        this.searchForm.elements["source"].value = clickType;
        this.searchForm.submit();
    };

    componentWillLoad() {
        this.initialSearchString = this.searchString;
        this.hasText = this.searchString.length > 0;
        this.setSearchForm();
        this.setSearchInput();
        this.setNavTabContainer();
        this.setResultTabContainer();
    }

    componentDidLoad() {
        if (!this.noInitialFocus) {
            this.searchInput.focus();
        }
    }

    disconnectedCallback() {
        if (this.searchForm !== null) {
            this.searchForm.removeEventListener("submit", this.handleFormEvent);
        }
    }

    private scrollToResults = () => {
        if (this.resultTabContainer === null) {
            return;
        }
        const bar = this.domNode.querySelector(".ux-iws-search-bar__inner");
        const offsetTop = bar.getBoundingClientRect().height - 30;
        scrollToTarget({
            target: this.resultTabContainer,
            additionalOffsetTop: offsetTop,
        });
    };

    /** Handle clicks on buttons slot (e.g. GPT button) */
    private handleClickExtraButton = (event) => {
        const dataAttribute = "data-search-tab";
        const target = (event.target as HTMLElement).closest(
            `[${dataAttribute}]`
        );
        if (target !== null) {
            const tabId = target.getAttribute(dataAttribute);
            /**
             * We don't want the ux-tab-container to handle the click event for targets with
             * data attribute "data-ux-tab-target"
             */
            event.stopPropagation();
            event.preventDefault();
            /**
             * If the search string has changed since last page load, the form is submitted
             * with an additional query parameter to activate the according tab
             */
            if (
                this.searchInput.value !== this.initialSearchString &&
                this.searchForm !== null
            ) {
                let activeTabInput =
                    this.searchForm.querySelector<HTMLInputElement>(
                        "input[name=active-tab]"
                    );
                if (activeTabInput !== null) {
                    activeTabInput.value = tabId;
                }
                activeTabInput =
                    this.searchForm.querySelector<HTMLInputElement>(
                        `input[name="tx_solr[active-tab]"]`
                    );
                if (activeTabInput !== null) {
                    activeTabInput.value = tabId;
                }
                this.searchForm.submit();
                return;
            }
            /**
             * Scroll to the search results tab container with while respecting the space reserved by
             * the search-bar
             */
            if (this.navTabContainer !== null) {
                this.navTabContainer.setActiveTab(tabId);
            } else if (this.resultTabContainer !== null) {
                this.resultTabContainer.setActiveTab(tabId);
            }
            this.scrollToResults();
        }
    };

    render() {
        const hasButtons =
            this.domNode.querySelector("[slot=buttons]") !== null;
        const hasIconOnlyButton = this.buttonText.length === 0;
        return (
            <Host
                class={{
                    [baseClass]: true,
                    ["is-sticky"]: this.isSticky,
                    [`${baseClass}--compact`]: this.compact,
                    [`${baseClass}--no-padding`]: this.noPadding,
                    [`${baseClass}--with-autosuggest`]: this.showAutosuggest,
                    [`${baseClass}--icon-only`]: hasIconOnlyButton,
                }}
            >
                <div class={`${baseClass}__inner`}>
                    <div class={`${baseClass}__controls`}>
                        <div class={`${baseClass}__field`}>
                            <input
                                class="ux-input tx-solr-q ui-autocomplete-input"
                                name={this.name}
                                id={this.searchPreviewFieldId}
                                placeholder={this.placeholder}
                                type="text"
                                ref={(el) => (this.searchInput = el)}
                                defaultValue={this.searchString}
                                onChange={this.handleFormEvent}
                                onInput={this.handleFormEvent}
                                data-testid="search-bar-input"
                                aria-label={
                                    this.searchInputLabel || this.placeholder
                                }
                            />
                            <input type="hidden" name="source" value="input" />
                            <button
                                type="button"
                                class={{
                                    [`${baseClass}__reset-btn`]: true,
                                    [`${baseClass}__reset-btn--is-active`]:
                                        this.hasText,
                                }}
                                onClick={this.resetForm}
                                data-testid="reset-search-bar"
                                aria-label={this.searchResetLabel}
                            >
                                <ux-icon name="close" />
                            </button>
                        </div>
                        <span class={`${baseClass}__spacer`} />
                        <ux-button
                            type="submit"
                            variant="primary"
                            data-testid="submit-search-bar"
                            class={`${baseClass}__search-button`}
                        >
                            <ux-icon name="search" />
                            <span class={`${baseClass}__search-button__label`}>
                                {this.buttonText}
                            </span>
                        </ux-button>
                        <div
                            class={{
                                [`${baseClass}__buttons`]: hasButtons,
                                [`${baseClass}__hidden`]: !hasButtons,
                            }}
                            onClick={
                                hasButtons ? this.handleClickExtraButton : null
                            }
                        >
                            <slot name="buttons" />
                        </div>
                    </div>
                    {/* Autosuggest */}
                    <div
                        class={`${baseClass}__autosuggest`}
                        style={!this.showAutosuggest ? { display: "none" } : {}}
                    >
                        {this.autosuggestEndpoint !== "" &&
                            this.autosuggestEndpoint !== "undefined" && (
                                <ux-iws-autosuggest
                                    searchString={this.searchString}
                                    resultsLabel={this.resultsLabel}
                                    quickLinksLabel={this.quickLinksLabel}
                                    curatedSearchLabel={this.curatedSearchLabel}
                                    quickLinksEndpoint={this.quickLinksEndpoint}
                                    autosuggestEndpoint={
                                        this.autosuggestEndpoint
                                    }
                                    onUxAutosuggestClick={
                                        this.handleAutosuggestionBtnClick
                                    }
                                ></ux-iws-autosuggest>
                            )}
                    </div>
                    <div
                        class={`${baseClass}__content`}
                        style={this.noContent ? { display: "none" } : {}}
                    >
                        <slot />
                    </div>
                    <div class={`${baseClass}__overlay`}></div>
                </div>
                <div
                    class={`${baseClass}__blocker`}
                    style={{
                        height: `${this.isSticky ? this.barHeight : 0}px`,
                    }}
                />
            </Host>
        );
    }
}
